import { Dispatch, SetStateAction } from 'react';
import { DehydratedState } from '@tanstack/react-query';

export function assertUnreachable(arg: never): never {
	throw new Error(`Didn't expect arg [${arg}] to get here`);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function safelyAssertUnreachable(arg: never): null {
	return null;
}

export type Primitive = string | number | boolean | null | undefined;

export type Maybe<Value> = Value | undefined | null;

export type PromiseThenType<T> = T extends PromiseLike<infer U> ? PromiseThenType<U> : T;

export type ArrayElementType<T extends ReadonlyArray<unknown>> =
	T extends ReadonlyArray<infer ElementType> ? ElementType : never;

export function isNum(input: unknown): input is number {
	return typeof input === 'number' && !Number.isNaN(input);
}

export function isString(input: unknown): input is string {
	return typeof input === 'string';
}

export function isNil<T>(val: T | null | undefined): val is null | undefined {
	return val === undefined || val === null;
}

export function isNotNil<T>(val: T | null | undefined): val is T {
	return !isNil(val);
}

export function isNumber(val: unknown): val is number {
	return typeof val === 'number';
}

export type SetState<T> = Dispatch<SetStateAction<T>>;

type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never };
export type Exclusive<T, U> =
	T | U extends Record<string, unknown> ? (Without<T, U> & U) | (Without<U, T> & T) : T | U;

export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type RequiredBy<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

type NonNullableFields<T> = {
	[P in keyof T]: NonNullable<T[P]>;
};

export type NonNullableField<T, K extends keyof T> = T & NonNullableFields<Pick<T, K>>;

export interface PagePrefetchProps {
	errorStatusCode?: number | null;
	// NOTE: hydration is handled in src/pages/_app.tsx
	dehydratedState?: DehydratedState | null;
}
